import { Excel2UserItem } from "../interfaces/Excel2UserItem";

// Single source of truth for the Excel2 / ESEF privilege inheritance.
//
// The backend (Excel2UserController / getExcel2UserInfo) stores and returns the flags
// verbatim - it does NOT expand inheritance. The cascade is applied here so both the
// admin view (display) and any gating share the exact same rules.
//
// Excel2: isAdmin (super admin, from users.json - see the note on Excel2UserItem)
//         sits above everything and grants the lot. Below it isExcel2Admin is the
//         umbrella: it grants both product tracks AND the whole ESEF stair - ESEF
//         Admin is subordinate to Excel2 Admin. Web and Word are independent of each
//         other. (isUser is automatic for every portal user and is never stored or
//         administered.)
//
//     isAdmin  =>  isExcel2Admin
//     isExcel2Admin  =>  isExcel2WebAdmin  +  isExcel2WordAdmin  +  isESEFAdmin(=> whole stair)
//
// ESEF: a straight stair, higher grants everything lower.
//
//     isESEFAdmin  =>  isESEFTaxonomist  =>  isESEFTagger  =>  isESEFTextEditor

// The ESEF level a user holds, highest to lowest. 'none' means no ESEF access.
export type EsefLevel = 'admin' | 'taxonomist' | 'tagger' | 'texteditor' | 'none';

// ESEF stair ordered from highest to lowest. Index = rank (0 is highest).
export const ESEF_LEVELS: { key: Exclude<EsefLevel, 'none'>; field: keyof Excel2UserItem; label: string }[] = [
	{ key: 'admin', field: 'isESEFAdmin', label: 'ESEF Admin' },
	{ key: 'taxonomist', field: 'isESEFTaxonomist', label: 'ESEF Taxonomist' },
	{ key: 'tagger', field: 'isESEFTagger', label: 'ESEF Tagger' },
	{ key: 'texteditor', field: 'isESEFTextEditor', label: 'ESEF Text Editor' },
];

// The effective (inheritance-expanded) capabilities of an Excel2 user.
export interface Excel2Capabilities {
	// Super admin. Grants everything below, including excel2Admin.
	admin: boolean;
	excel2Admin: boolean;
	excel2Web: boolean;
	excel2Word: boolean;
	esefAdmin: boolean;
	esefTaxonomist: boolean;
	esefTagger: boolean;
	esefTextEditor: boolean;
	// Highest ESEF level held, for compact display / radio selection.
	esefLevel: EsefLevel;
}

// The highest ESEF level the (unexpanded) stored flags represent.
export const getEsefLevel = (item: Excel2UserItem): EsefLevel => {
	for (const level of ESEF_LEVELS) {
		if (item[level.field]) return level.key;
	}
	return 'none';
};

// Expand the stored flags into effective capabilities, applying inheritance.
export const expandExcel2Privileges = (item: Excel2UserItem): Excel2Capabilities => {
	const admin = !!item.isAdmin;
	// Super admin outranks the Excel2 umbrella, which in turn forces the ESEF stair.
	const excel2Admin = admin || !!item.isExcel2Admin;
	// Excel2 Admin sits above ESEF Admin, so it forces the effective ESEF level to the
	// top regardless of the stored ESEF flags.
	const esefLevel: EsefLevel = excel2Admin ? 'admin' : getEsefLevel(item);
	// Rank of the highest held ESEF level; anything at or below that rank is granted.
	const esefRank = ESEF_LEVELS.findIndex((l) => l.key === esefLevel); // -1 when 'none'

	const holdsEsef = (key: Exclude<EsefLevel, 'none'>): boolean => {
		if (esefRank < 0) return false;
		return ESEF_LEVELS.findIndex((l) => l.key === key) >= esefRank;
	};

	return {
		admin,
		excel2Admin,
		excel2Web: excel2Admin || !!item.isExcel2WebAdmin,
		excel2Word: excel2Admin || !!item.isExcel2WordAdmin,
		esefAdmin: holdsEsef('admin'),
		esefTaxonomist: holdsEsef('taxonomist'),
		esefTagger: holdsEsef('tagger'),
		esefTextEditor: holdsEsef('texteditor'),
		esefLevel,
	};
};

// Set the ESEF stair to a single chosen level, clearing the others. The stored flags
// hold exactly one ESEF level (the chosen one); inheritance is derived on read via
// expandExcel2Privileges, so we do not persist the expanded set.
export const setEsefLevel = (item: Excel2UserItem, level: EsefLevel): Excel2UserItem => {
	const next: Excel2UserItem = { ...item };
	for (const l of ESEF_LEVELS) {
		(next[l.field] as boolean) = l.key === level;
	}
	return next;
};

// True when the user has any Excel2 or ESEF capability at all.
export const hasAnyExcel2Privilege = (item: Excel2UserItem): boolean => {
	const caps = expandExcel2Privileges(item);
	return caps.excel2Admin || caps.excel2Web || caps.excel2Word || caps.esefLevel !== 'none';
};
